/**
 * TeamMemberView - Standalone view for worship team members (non-admins)
 * 
 * This is the same TeamCommsApp that admins see, but as a standalone page
 * accessible at /team route. Includes push notification support.
 */
import React, { useEffect, useState } from 'react';
import { TeamCommsApp } from '../comms';
import { requestNotificationPermission, onForegroundMessage, isPushSupported, refreshPushSubscription } from '../../lib/firebase';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { OfflineBanner } from '../shared/OfflineBanner';

function TeamMemberView({ 
  user, 
  onLogout, 
  globalPosts, 
  setGlobalPosts,
  globalTasks,
  setGlobalTasks,
  // Deep linking props for notification navigation
  initialTab,
  initialConversationId,
  highlightPostId,
  highlightCommentId,
  highlightMessageId,
  // Notification hook — single source of truth
  notificationHook,
  markMessagesAsRead,
  // Fix 7 G4: a Services tap opens the Services view with that service highlighted
  serviceHighlight = null
}) {
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const { isOnline, wasOffline } = useOnlineStatus();

  // Clean up URL params after component mounts (params already read by App.js)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('tab') || urlParams.has('post') || urlParams.has('conversation')) {
      // Small delay to ensure props are passed before cleaning URL
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    }
  }, []);

  // Initialize push notifications
  useEffect(() => {
    if (isPushSupported() && user?.id) {
      const hasAsked = localStorage.getItem('pushNotificationAsked');
      
      // CRITICAL FOR iOS: Refresh subscription on every app launch
      // iOS aggressively expires push subscriptions
      if (Notification.permission === 'granted') {
        refreshPushSubscription(user.id);
      }
      
      // Show prompt if never asked
      if (!hasAsked) {
        // Show prompt after a short delay
        setTimeout(() => setShowNotificationPrompt(true), 1500);
      }
      
      // Set up foreground message handler
      const unsubscribe = onForegroundMessage((payload) => {
        // Don't show toast if we're already viewing the relevant content
        const data = payload.data || {};
        const shouldSuppress = 
          (data.post_id && data.post_id === highlightPostId) ||
          (data.conversation_id && data.conversation_id === initialConversationId);
        
        if (!shouldSuppress) {
          toast(payload.notification?.title || 'New Notification', {
            description: payload.notification?.body,
            action: {
              label: 'View',
              onClick: () => {
                // Handle navigation based on notification type
                if (data.type === 'dm' && data.conversation_id) {
                  window.location.href = `/team?tab=messages&conversation=${data.conversation_id}`;
                } else if (data.post_id) {
                  window.location.href = `/team?tab=feed&post=${data.post_id}`;
                }
              }
            }
          });
        }
      });
      
      return () => unsubscribe && unsubscribe();
    }
  }, [user?.id, highlightPostId, initialConversationId]);

  // Enable push notifications
  const enablePushNotifications = async () => {
    if (user?.id) {
      const token = await requestNotificationPermission(user.id);
      if (token) {
        setPushEnabled(true);
        toast.success('Notifications enabled!');
        localStorage.setItem('pushNotificationDeclined', 'false');
      } else {
        toast.error('Could not enable notifications. Please check your browser settings.');
      }
    }
    localStorage.setItem('pushNotificationAsked', 'true');
    setShowNotificationPrompt(false);
  };

  // Dismiss notification prompt
  const dismissNotificationPrompt = () => {
    localStorage.setItem('pushNotificationAsked', 'true');
    localStorage.setItem('pushNotificationDeclined', 'true');
    setShowNotificationPrompt(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#1e1f22] relative overflow-x-hidden">
      {/* Push Notification Prompt - Fixed at top, above header */}
      {showNotificationPrompt && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 py-3 pt-[max(12px,calc(env(safe-area-inset-top)+8px))]">
          <div className="max-w-lg mx-auto">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Stay Updated</p>
                  <p className="text-xs opacity-90">Get notified of new posts and messages</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-10 sm:ml-0">
                <Button 
                  size="sm" 
                  onClick={enablePushNotifications}
                  className="bg-card text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/10 h-8 text-xs font-semibold px-4 rounded-lg shadow-sm"
                >
                  Enable
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed notification banner */}
      {showNotificationPrompt && (
        <div className="h-[70px] pt-[env(safe-area-inset-top)]" />
      )}

      {/* Offline Banner */}
      <OfflineBanner isOnline={isOnline} wasOffline={wasOffline} />

      {/* Team Comms App - Full Screen (Feed Only) */}
      <TeamCommsApp 
        user={user} 
        onBack={onLogout}
        isStandalone={true}
        globalPosts={globalPosts}
        setGlobalPosts={setGlobalPosts}
        globalTasks={globalTasks}
        setGlobalTasks={setGlobalTasks}
        isTeamMemberView={true}
        initialView={initialTab || 'feed'}
        initialConversationId={initialConversationId}
        highlightPostId={highlightPostId}
        highlightCommentId={highlightCommentId}
        initialMessageId={highlightMessageId}
        isOnline={isOnline}
        notificationHook={notificationHook}
        markMessagesAsRead={markMessagesAsRead}
        serviceHighlight={serviceHighlight}
      />
    </div>
  );
}

export default TeamMemberView;
